import { type AnyRecord, REVIEW_REASON, type WorkflowKind } from '@fruuit/shared'
import { Button, Callout, Dialog, DialogCancel, Textarea, useToast } from '@fruuit/ui'
import { useId, useState } from 'react'
import { useApiMutation } from '../../api/hooks'
import { recordsApi, useInvalidateRecords } from './api'
import { serverErrors } from './form'

export interface CorrectionDialogProps {
  open: boolean
  kind: WorkflowKind
  recordId: string
  version: number
  onClose: () => void
  /** Receives the new draft version (navigate to it). */
  onCorrected?: (record: AnyRecord) => void
}

/**
 * P-WF-003: published records are immutable, so a correction starts a new draft version
 * linked to the published one, with a reason (FORM-008).
 */
export function CorrectionDialog(props: CorrectionDialogProps) {
  if (!props.open) return null
  return <CorrectionDialogBody {...props} />
}

function CorrectionDialogBody({
  kind,
  recordId,
  version,
  onClose,
  onCorrected,
}: CorrectionDialogProps) {
  const formId = useId()
  const { toast } = useToast()
  const invalidate = useInvalidateRecords()
  const [reason, setReason] = useState('')
  const [fieldError, setFieldError] = useState<string | null>(null)
  const [formError, setFormError] = useState<string | null>(null)
  const mutation = useApiMutation(
    (body: { change_reason: string }, ctx) => recordsApi.correct(kind, recordId, body, ctx),
    {
      onSuccess: async (record) => {
        await invalidate()
        toast({
          tone: 'success',
          message:
            'Corrected version created as a draft. The published version stays visible until the correction is published.',
        })
        onCorrected?.(record as AnyRecord)
        onClose()
      },
      onError: (error) => {
        const parsed = serverErrors(error)
        setFieldError(parsed.fields.change_reason ?? null)
        setFormError(parsed.fields.change_reason ? null : parsed.form)
      },
    },
  )

  return (
    <Dialog
      open
      onClose={onClose}
      size="sm"
      title={`Correct version ${version}`}
      description="The server assigns the next version number and links it to this published version."
      footer={
        <>
          <DialogCancel />
          <Button type="submit" form={formId} loading={mutation.isPending}>
            Create corrected draft
          </Button>
        </>
      }
    >
      <form
        id={formId}
        noValidate
        className="flex flex-col gap-4"
        onSubmit={(event) => {
          event.preventDefault()
          const trimmed = reason.trim()
          if (trimmed.length < REVIEW_REASON.min) {
            setFieldError(`Explain the correction in at least ${REVIEW_REASON.min} characters.`)
            return
          }
          setFieldError(null)
          setFormError(null)
          mutation.mutate({ change_reason: trimmed })
        }}
      >
        {formError ? (
          <Callout tone="danger" role="alert">
            {formError}
          </Callout>
        ) : null}
        <Textarea
          label="Reason for the correction"
          hint="Shown with the version history. Clients keep seeing the published version until the correction is published."
          required
          rows={4}
          maxLength={REVIEW_REASON.max}
          value={reason}
          error={fieldError}
          disabled={mutation.isPending}
          onChange={(event) => setReason(event.target.value)}
        />
      </form>
    </Dialog>
  )
}
